import { isValidElement, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { CloseButton } from '../../button/CloseButton';
import { Text } from '../../text';
import type { BannerType, BannerVariantProps } from '../_lib/variants';
import { bannerVariants } from '../_lib/variants';
import { BannerIcon } from './BannerIcon';

type BannerProps = {
  /** バナーを表示するか */
  showBanner?: boolean;
  /** メインテキスト */
  title: string;
  /** サブテキスト（オプション） */
  description?: ReactNode;
  /** バナーのタイプ */
  type?: BannerType;
  showTypeIcon?: boolean;
  rightItem?: ReactNode;
  closeIcon?: {
    onClick: () => void;
  };
  /** カスタムクラス */
  className?: string;
} & BannerVariantProps;

/**
 * Bannerコンポーネント
 *
 * 情報・警告・エラーメッセージを表示するためのバナーコンポーネント
 *
 * @example
 * ```tsx
 * <Banner
 *   title="Line 1"
 *   description="Line 2"
 *   type="neutral"
 *   showTypeIcon
 * />
 * ```
 */
export const Banner = ({
  showBanner = true,
  title,
  description,
  showTypeIcon = true,
  type = 'neutral',
  closeIcon,
  rightItem,
  className,
}: BannerProps) => {
  if (!showBanner) return null;
  return (
    <div className={cn(bannerVariants({ type }), className)} role="alert">
      <div className="flex flex-1 items-start gap-3">
        {showTypeIcon && <BannerIcon type={type} />}
        <div className="flex flex-1 flex-col gap-px text-sm leading-text-sm">
          <Text variant="label">{title}</Text>
          {!description ? null : isValidElement(description) ? (
            description
          ) : (
            <Text variant="description">{description}</Text>
          )}
        </div>
        {closeIcon && <CloseButton onClick={closeIcon.onClick} />}
      </div>
      {rightItem}
    </div>
  );
};
